import { Component, Element, Prop, h, Listen, State } from '@stencil/core';
import { AiIconType } from '../../utils/icon-types';

@Component({
  tag: 'biel-chat',
  styleUrl: 'biel-chat.css',
  shadow: true,
})
export class BielChat {
  @State() isUserScrollingUp: boolean = false;
  @State() isStreaming: boolean = false;

  private scrollbarHideTimer: ReturnType<typeof setTimeout> | null = null;
  private scrollRafId: number | null = null;
  private isInitialLoad: boolean = true;
  private lastUserScrollInputAt: number = 0;
  private contentResizeObserver: ResizeObserver | null = null;

  // How long after a wheel/pointer/key input a scroll event is still
  // attributed to the user. Generous enough to cover touch momentum, which
  // keeps firing scroll events after the finger lifts.
  private static readonly USER_SCROLL_INPUT_WINDOW_MS = 2500;

  @Prop() messages: {
    text: string;
    sender: 'user' | 'ai';
    messageId?: string;
    feedback?: 0 | 1;
    sources?: { title: string; url: string }[];
    isPartial: boolean;
    partialId?: number;
    incomplete?: boolean;
  }[] = [];
  @Prop() isFullscreen: boolean;
  @Prop() isLoading: boolean = false;
  @Prop() suggestedQuestions: string[];
  @Prop() welcomeMessage: string;
  @Prop() suggestedQuestionsTitle: string;
  @Prop() loadedChat: boolean;
  @Prop() hideFeedback: boolean;
  @Prop() modalPosition: string;
  @Prop() embeddedMode: boolean = false;
  @Prop() bielId: string;
  @Prop() sourcesText: string = 'Sources';
  @Prop() aiIcon: AiIconType = 'biel';
  @Prop() hideAvatars: boolean = false;
  @Prop() hideSources: boolean = false;
  @Prop() showContinueButton: boolean = false;
  @Prop() assistantLabel: string = 'Assistant';
  // All actions configured on the project; forwarded to each AI message so
  // inline <action/> tags resolve to chips during streaming.
  @Prop() projectActions: { name: string; button_text: string; url: string }[] = [];
  @Prop() smoothStreaming: boolean = true;

  @Element() el: HTMLElement;

  private chatContainer: HTMLElement;

  @Listen('addMessageText', { target: 'body' })
  handleTextUpdate(event: CustomEvent<{ text: string; id: number; bielId: string }>) {
    if (this.bielId === event.detail.bielId) {
      this.isStreaming = true;
      if (!this.isUserScrollingUp) {
        this.scheduleScrollToBottom();
      }
    }
  }

  @Listen('partialMessageComplete', { target: 'body' })
  handlePartialMessageComplete(event: CustomEvent<{ partialId: number; bielId: string }>) {
    if (this.bielId === event.detail.bielId) {
      if (!this.isUserScrollingUp) {
        this.scheduleScrollToBottom();
        // Force scroll after sources/feedback have rendered and painted
        setTimeout(() => {
          if (!this.isUserScrollingUp) {
            this.scrollToBottom();
          }
        }, 300);
      }
      setTimeout(() => {
        this.isStreaming = false;
      }, 200);
    }
  }

  @Listen('scrollToBottom', { target: 'body' })
  handleScrollToBottom(event: CustomEvent<{ bielId: string }>) {
    // Ignore events fired by biel-message during initial historical message render
    if (this.bielId === event.detail.bielId && !this.isInitialLoad) {
      if (!this.isUserScrollingUp) {
        this.scheduleScrollToBottom();
      }
    }
  }

  @Listen('messageSent', { target: 'body' })
  handleMessageSent(event: CustomEvent<{ bielId: string }>) {
    if (this.bielId === event.detail.bielId) {
      this.resumeAutoScroll();
    }
  }

  @Listen('suggestedQuestionClick', { target: 'body' })
  handleSuggestedQuestionClick(event: CustomEvent<{ bielId: string }>) {
    if (this.bielId === event.detail.bielId) {
      this.resumeAutoScroll();
    }
  }

  // Sending a new message re-engages the conversation: pin to the bottom for
  // the upcoming answer even if the user had scrolled up while reading.
  private resumeAutoScroll() {
    this.isUserScrollingUp = false;
    this.scheduleScrollToBottom();
  }

  scheduleScrollToBottom() {
    if (this.scrollRafId !== null) {
      cancelAnimationFrame(this.scrollRafId);
    }
    this.scrollRafId = requestAnimationFrame(() => {
      this.scrollRafId = null;
      this.scrollToBottom();
    });
  }

  scrollToBottom() {
    const container =
      this.chatContainer ?? this.el.shadowRoot.querySelector('.biel-chat-container');
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }

  private markUserScrollInput = () => {
    this.lastUserScrollInputAt = Date.now();
  };

  // A click on message content (links, action chips, suggested questions) is
  // not scrolling — only a press on the scrollbar gutter counts, so dragging
  // the scrollbar can still pause auto-scroll. The gutter belongs to the
  // container itself (clientWidth excludes the scrollbar), so the press must
  // target the container: offsetX on a bubbled child event is measured
  // against the child's own box, not the container's.
  private onPointerDown = (event: PointerEvent) => {
    if (
      this.chatContainer &&
      event.target === this.chatContainer &&
      event.offsetX >= this.chatContainer.clientWidth
    ) {
      this.markUserScrollInput();
    }
  };

  private onScroll = () => {
    if (!this.chatContainer) return;
    const { scrollTop, scrollHeight, clientHeight } = this.chatContainer;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 100;
    if (isAtBottom) {
      this.isUserScrollingUp = false;
    } else if (Date.now() - this.lastUserScrollInputAt < BielChat.USER_SCROLL_INPUT_WINDOW_MS) {
      // Only user-driven scrolling may pause auto-scroll. Scroll events also
      // fire for layout-driven adjustments while an answer streams in (scroll
      // anchoring reacting to async code-snippet highlighting, images, action
      // chips); treating those as the user scrolling up would silently stop
      // auto-scroll for the rest of the response.
      this.isUserScrollingUp = true;
    }

    // Show scrollbar on manual scroll (not during streaming), hide after 1s of inactivity
    if (!this.isStreaming || this.isUserScrollingUp) {
      this.chatContainer.classList.add('is-scrolling');
      if (this.scrollbarHideTimer) clearTimeout(this.scrollbarHideTimer);
      this.scrollbarHideTimer = setTimeout(() => {
        this.scrollbarHideTimer = null;
        this.chatContainer?.classList.remove('is-scrolling');
      }, 1000);
    }
  };

  componentDidLoad() {
    this.chatContainer = this.el.shadowRoot.querySelector('.biel-chat-container');
    if (this.chatContainer) {
      this.chatContainer.addEventListener('scroll', this.onScroll);
      this.chatContainer.addEventListener('wheel', this.markUserScrollInput, { passive: true });
      this.chatContainer.addEventListener('pointerdown', this.onPointerDown, {
        passive: true,
      });
      this.chatContainer.addEventListener('touchmove', this.markUserScrollInput, {
        passive: true,
      });
      this.chatContainer.addEventListener('keydown', this.markUserScrollInput, {
        passive: true,
      });

      // Content keeps growing after the chunk that produced it was scrolled
      // (async code-snippet highlighting, image loads, action chips). Re-pin
      // the bottom on any message resize unless the user scrolled away.
      if (typeof ResizeObserver !== 'undefined') {
        this.contentResizeObserver = new ResizeObserver(() => {
          if (!this.isUserScrollingUp) {
            this.scheduleScrollToBottom();
          }
        });
        this.observeContent();
      }
    }

    // Always scroll to bottom on mount, after the widget open animation (300ms).
    setTimeout(() => {
      this.isInitialLoad = false;
      this.scrollToBottom();
    }, 300);
  }

  componentDidUpdate() {
    this.observeContent();
  }

  // Observe newly added children — the message list only grows, and
  // ResizeObserver doesn't track added nodes by itself. Observed targets are
  // held weakly, so detached children (e.g. after a chat reset) need no
  // explicit unobserve.
  private observedChildren = new WeakSet<Element>();

  private observeContent() {
    if (!this.contentResizeObserver || !this.chatContainer) return;
    for (const child of Array.from(this.chatContainer.children)) {
      if (!this.observedChildren.has(child)) {
        this.observedChildren.add(child);
        this.contentResizeObserver.observe(child);
      }
    }
  }

  disconnectedCallback() {
    if (this.chatContainer) {
      this.chatContainer.removeEventListener('scroll', this.onScroll);
      this.chatContainer.removeEventListener('wheel', this.markUserScrollInput);
      this.chatContainer.removeEventListener('pointerdown', this.onPointerDown);
      this.chatContainer.removeEventListener('touchmove', this.markUserScrollInput);
      this.chatContainer.removeEventListener('keydown', this.markUserScrollInput);
    }
    if (this.contentResizeObserver) {
      this.contentResizeObserver.disconnect();
      this.contentResizeObserver = null;
    }
    if (this.scrollbarHideTimer) clearTimeout(this.scrollbarHideTimer);
    if (this.scrollRafId !== null) cancelAnimationFrame(this.scrollRafId);
  }

  render() {
    return (
      <div
        class={`biel-chat-container ${this.embeddedMode ? 'biel-chat-container--embedded' : ''} ${this.isFullscreen ? 'fullscreen' : ''} ${this.modalPosition} ${this.isStreaming ? 'is-streaming' : ''}`}
        part="container"
      >
        {this.welcomeMessage && (
          <biel-message
            text={this.welcomeMessage}
            sender="ai"
            sourcesText={this.sourcesText}
            aiIcon={this.aiIcon}
            hideAvatars={this.hideAvatars}
            hideSources={this.hideSources}
            assistantLabel={this.assistantLabel}
            exportparts="container: biel-message-container, label: biel-message-label, bubble: biel-message-bubble, content: biel-message-content, footer: biel-message-footer, sources-toggle: biel-message-sources-toggle, actions: biel-message-actions, copy-button: biel-message-copy-button, thumb-up: biel-message-thumb-up, thumb-down: biel-message-thumb-down, sources-panel: biel-message-sources-panel, source-card: biel-message-source-card, source-badge: biel-message-source-badge, source-title: biel-message-source-title, source-host: biel-message-source-host"
          ></biel-message>
        )}
        {this.suggestedQuestions && this.suggestedQuestions.length > 0 && (
          <biel-suggested-questions
            heading={this.suggestedQuestionsTitle}
            questions={this.suggestedQuestions}
            bielId={this.bielId}
            exportparts="container: biel-suggested-questions-container, title: biel-suggested-questions-title, list: biel-suggested-questions-list, question: biel-suggested-questions-question"
          ></biel-suggested-questions>
        )}
        {this.messages.map((message) => (
          <biel-message
            messageId={message.messageId}
            text={message.text}
            sender={message.sender}
            sources={message.sources}
            availableActions={this.projectActions}
            feedback={message.feedback}
            hideFeedback={this.hideFeedback}
            bielId={this.bielId}
            isPartial={message.isPartial}
            partialId={message.partialId}
            smoothStreaming={this.smoothStreaming}
            sourcesText={this.sourcesText}
            aiIcon={this.aiIcon}
            hideAvatars={this.hideAvatars}
            hideSources={this.hideSources}
            assistantLabel={this.assistantLabel}
            exportparts="container: biel-message-container, label: biel-message-label, bubble: biel-message-bubble, content: biel-message-content, footer: biel-message-footer, sources-toggle: biel-message-sources-toggle, actions: biel-message-actions, copy-button: biel-message-copy-button, thumb-up: biel-message-thumb-up, thumb-down: biel-message-thumb-down, sources-panel: biel-message-sources-panel, source-card: biel-message-source-card, source-badge: biel-message-source-badge, source-title: biel-message-source-title, source-host: biel-message-source-host"
          ></biel-message>
        ))}
        {this.showContinueButton && (
          <div class="biel-continue-button-container" part="continue-container">
            <button
              part="continue-button"
              class="biel-continue-button"
              onClick={() => {
                const event = new CustomEvent('continueClicked', {
                  detail: { bielId: this.bielId },
                  bubbles: true,
                });
                document.body.dispatchEvent(event);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-fast-forward-icon lucide-fast-forward"
              >
                <path d="M12 6a2 2 0 0 1 3.414-1.414l6 6a2 2 0 0 1 0 2.828l-6 6A2 2 0 0 1 12 18z" />
                <path d="M2 6a2 2 0 0 1 3.414-1.414l6 6a2 2 0 0 1 0 2.828l-6 6A2 2 0 0 1 2 18z" />
              </svg>
              Continue generating
            </button>
          </div>
        )}
        {this.isLoading && (
          <div class="loading-animation" part="loading">
            <div class="dot"></div>
            <div class="dot"></div>
            <div class="dot"></div>
          </div>
        )}
      </div>
    );
  }
}
